import {
    CompanyConditionsForm,
    CompanyDetailsForm,
    CompanyKnowledgeForm,
    CompanyOpenSourceForm,
    CompanyPrinciplesForm,
    CompanyProfileForm,
    CompanyProfileStep,
    InstitutionDetailsForm,
    InstitutionProfileForm,
    InstitutionProfileStep
} from '@dh/screens/edit-institution/types';
import { RUpdateInstitutionBody } from '@dh/types';
import { makeStrapiRelation, makeStrapiRelations } from '@dh/utils';

const mapDetailsFormToBody = (form: InstitutionDetailsForm): RUpdateInstitutionBody => {
    if (!form.name.value)
        throw new Error('Mandatory values are missing in the data you tried to submit!');

    return {
        name: form.name.value,
        slogan: form.slogan.value,
        infoTitle: form.infoTitle.value,
        info: form.infoText.value,
        email: form.email.value,
        phone: form.telephoneNumber.value,
        website: form.website.value,
        fullAddress: form.address.value?.fullAddress,
        addressLongitude: form.address.value?.coordinates[0],
        addressLatitude: form.address.value?.coordinates[1]
    };
};

export const mapEditInstitutionFormToBody = (
    form: Partial<InstitutionProfileForm>
): RUpdateInstitutionBody => {
    return form[InstitutionProfileStep.Details]
        ? mapDetailsFormToBody(form[InstitutionProfileStep.Details])
        : {};
};

const mapCompanyDetailsFormToBody = (form: CompanyDetailsForm): RUpdateInstitutionBody => {
    const generalDetails = mapDetailsFormToBody(form);
    return {
        ...generalDetails,
        isPublic: form.isPublic.value
        // TODO: Add Software to CMS (PRMA-66)
        // services: makeStrapiRelations(form.services.value)
    };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mapCompanyKnowledgeFormToBody = (form: CompanyKnowledgeForm): RUpdateInstitutionBody => {
    if (!form) return {};
    return {
        topicsKnowledge: form.topicsKnowledge.value
            // we want to store only topics where we have knowledge
            ?.filter((topicKnowledge) => topicKnowledge.knowledge > 0)
            .map((topicKnowledge) => ({
                topic: makeStrapiRelation(topicKnowledge.topic),
                knowledge: topicKnowledge.knowledge
            }))
    };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mapCompanyPrinciplesFormToBody = (form: CompanyPrinciplesForm): RUpdateInstitutionBody => {
    if (!form) return {};
    return {
        principles: makeStrapiRelations(form.principles.value)
    };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mapCompanyConditionsFormToBody = (form: CompanyConditionsForm): RUpdateInstitutionBody => {
    if (!form) return {};
    let result: RUpdateInstitutionBody = {};
    if (form.minBudget.value !== undefined) {
        result = {
            ...result,
            budget: {
                min: form.minBudget.value
            }
        };
    }
    if (form.languages.value) {
        result = {
            ...result,
            languages: makeStrapiRelations(form.languages.value)
        };
    }
    return result;
};

const mapCompanyOpenSourceFormToBody = (form: CompanyOpenSourceForm): RUpdateInstitutionBody => {
    if (!form || !form.software.value) return {};

    // Check for temp-* IDs that shouldn't be here (they should have been replaced in EditCompanyScreen)
    const hasTempIds = form.software.value.some((sw) => sw.documentId.startsWith('temp-'));
    if (hasTempIds) {
        console.error(
            '[mapCompanyOpenSourceFormToBody] Found temp-* IDs in software list! This indicates a bug in the save logic.',
            form.software.value.filter((sw) => sw.documentId.startsWith('temp-'))
        );
    }

    // Map software list to Strapi relations using documentIds
    const relations = makeStrapiRelations(form.software.value);

    return {
        used_software: relations
    };
};

export const mapEditCompanyFormToBody = (
    form: Partial<CompanyProfileForm>
): RUpdateInstitutionBody => {
    const updatedCompanyDetails = form[CompanyProfileStep.Details]
        ? mapCompanyDetailsFormToBody(form[CompanyProfileStep.Details])
        : {};

    const updatedExperiences = form[CompanyProfileStep.Knowledge]
        ? mapCompanyKnowledgeFormToBody(form[CompanyProfileStep.Knowledge])
        : {};

    const updatedValues = form[CompanyProfileStep.Principles]
        ? mapCompanyPrinciplesFormToBody(form[CompanyProfileStep.Principles])
        : {};

    const updatedConditions = form[CompanyProfileStep.Conditions]
        ? mapCompanyConditionsFormToBody(form[CompanyProfileStep.Conditions])
        : {};

    const updatedSoftware = form[CompanyProfileStep.OpenSource]
        ? mapCompanyOpenSourceFormToBody(form[CompanyProfileStep.OpenSource])
        : {};

    return {
        ...updatedCompanyDetails,
        ...updatedExperiences,
        ...updatedValues,
        ...updatedConditions,
        ...updatedSoftware
    };
};
